import React, { useState } from "react";

import { Typography, WorkerIcon as Worker } from "@packages/ds";
import { useIntl } from "react-intl";

import ServiceIcon from "Components/ServiceIcon";
import Select from "ds/Select";
import { Tooltip } from "ds/Tooltip";
import useMediaQuery from "Hooks/useMediaQuery";
import Error from "src/ds/Input/Error.styles";

import * as S from "./ResourceRow.style";

import type { ModifiedDeploymentService } from "Reducers/environment/deployment";

type Option = {
  value: string;
  label: string;
  disabled?: boolean;
};

interface ResourceRowProps {
  service?: ModifiedDeploymentService;
  alt: string;
  title: string;
  isWorker: boolean;
  formIsDisabled: boolean;
  // CPU & RAM
  cpuRamValue: Option;
  cpuRamOptions: Array<{ label: string; options: Option[] }>;
  cpuRamOnChange: (option) => void;
  cpuRamError?: boolean;
  // Instance
  instancesValue: Option;
  instancesOptions: Option[];
  instancesOnChange: (option: Option) => void;
  instancesOnlyText: string;
  // Disk
  diskValue: string | null;
  onDiskBlur: (newNumber: string, oldNumber: number | null) => void;
  diskOnChange: (newNumber: string, oldNumber: number | null) => void;
  diskOnlyText: string;
  diskError: boolean;
  diskTooltip: string;
  showTooltip: boolean;
}
const ResourceRow = ({
  alt,
  title,
  isWorker,
  formIsDisabled,
  service,
  // CPU & RAM
  cpuRamValue,
  cpuRamOptions,
  cpuRamOnChange,
  cpuRamError,
  // Instance
  instancesValue,
  instancesOptions,
  instancesOnChange,
  instancesOnlyText,
  // Disk
  diskValue,
  onDiskBlur,
  diskOnChange,
  diskOnlyText,
  diskError,
  diskTooltip,
  showTooltip
}: ResourceRowProps) => {
  const intl = useIntl();
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const lessThan720 = useMediaQuery(`(max-width: 720px)`);
  const showDiskPlaceholder = diskValue === null;

  const diskLabel = intl.formatMessage({
    id: `configure_resources.page.disk_n_storage`,
    defaultMessage: "Disk / storage"
  });

  const cleanDiskValue =
    diskValue != null ? parseFloat((+diskValue / 1024).toFixed(3)) : null;

  const readyDiskValue = cleanDiskValue ? `${cleanDiskValue}` : "";

  const invalidChars = ["e", "E", "+", "-"];

  const diskInput = (
    <S.InputWrapper>
      <S.Input
        placeholder={
          showDiskPlaceholder
            ? intl.formatMessage({
                id: "configure_resources.page.disk.placeholder",
                defaultMessage: "ex 512 MB"
              })
            : ""
        }
        onMouseEnter={() => {
          if (showTooltip) {
            setIsTooltipOpen(true);
          }
        }}
        onMouseLeave={() => {
          if (showTooltip) {
            setIsTooltipOpen(false);
          }
        }}
        id={`${alt}-${title}-disk`}
        label={lessThan720 ? diskLabel : undefined}
        value={readyDiskValue ?? ""}
        disabled={formIsDisabled}
        isValid={true}
        onBlur={e => {
          const value = +e.currentTarget.value;

          const roundedToNearestMb = Math.round(value * 1024);
          onDiskBlur(`${roundedToNearestMb}`, cleanDiskValue);
        }}
        onChange={e => {
          const value = +e.currentTarget.value;

          diskOnChange(`${value * 1024}`, cleanDiskValue);
        }}
        onKeyDown={evt =>
          invalidChars.includes(evt.key) && evt.preventDefault()
        }
        onPaste={evt => {
          invalidChars.includes(evt.clipboardData.getData("Text")) &&
            evt.preventDefault();
        }}
        width="100%"
        type="number"
        autoComplete="off"
        step="0.10"
      />
      {!showDiskPlaceholder && (
        <S.Blank gapSpace={readyDiskValue.length * 9} disabled={formIsDisabled}>
          {intl.formatMessage({
            id: "configure_resources.page.disk.units.label",
            defaultMessage: "GB"
          })}
        </S.Blank>
      )}
      {diskError && (
        <Error>
          {intl.formatMessage({
            id: `configure_resources.page.disk.error`,
            defaultMessage: "Disk is required"
          })}
        </Error>
      )}
    </S.InputWrapper>
  );

  // Show the tooltip only when the disk value hasn't been set/saved
  const diskComponent = showTooltip ? (
    <Tooltip
      tooltip={diskTooltip}
      side="bottom"
      align="end"
      open={isTooltipOpen}
      analyticId="disk_tooltip"
    >
      {diskInput}
    </Tooltip>
  ) : (
    diskInput
  );

  return (
    <S.ResourceRow>
      <S.ImageAndTitleWrapper indent={isWorker}>
        {isWorker ? (
          <Worker />
        ) : (
          <ServiceIcon service={service} alt={alt} title={alt} />
        )}
        <Typography.BodyText weight="semiBold" size="medium">
          {title}
        </Typography.BodyText>
      </S.ImageAndTitleWrapper>

      {/* RAM & CPU */}
      <S.SelectWrapper>
        <S.MobileLabel textStyle="default">
          {intl.formatMessage({
            id: `configure_resources.page.cpu_n_ram`,
            defaultMessage: "CPU & RAM"
          })}
        </S.MobileLabel>
        <Select
          placeholder={
            <S.PlaceholderText weight="regular" size="medium">
              {intl.formatMessage({
                id: "configure_resources.page.cpu_ram.placeholder",
                defaultMessage: "Select CPU and RAM"
              })}
            </S.PlaceholderText>
          }
          id="configure_resources.page.cpu_ram.placeholder"
          value={cpuRamValue.label ? cpuRamValue : undefined}
          options={cpuRamOptions}
          onChange={cpuRamOnChange}
          isOptionDisabled={option => !!option.disabled}
          isDisabled={formIsDisabled}
        />
        {cpuRamError && (
          <Error>
            {intl.formatMessage({
              id: `configure_resources.page.cpu_n_ram.error`,
              defaultMessage: "CPU and RAM is required"
            })}
          </Error>
        )}
      </S.SelectWrapper>

      {/* INSTANCES */}
      <S.SelectWrapper>
        <S.MobileLabel textStyle="default">
          {intl.formatMessage({
            id: `configure_resources.page.instances`,
            defaultMessage: "Instances"
          })}
        </S.MobileLabel>
        {instancesOnlyText ? (
          <S.NoFieldText weight="regular" size="medium">
            {instancesOnlyText}
          </S.NoFieldText>
        ) : (
          <Select
            value={instancesValue}
            options={instancesOptions}
            onChange={option => instancesOnChange(option as Option)}
            isMulti={false}
            isOptionDisabled={option => !!option.disabled}
            isDisabled={formIsDisabled}
            id="configure_resources.page.instances"
          />
        )}
      </S.SelectWrapper>

      {/* DISK */}
      {diskOnlyText ? (
        <S.SelectWrapper>
          <S.MobileLabel textStyle="default">{diskLabel}</S.MobileLabel>
          <S.NoFieldText weight="regular" size="medium">
            {diskOnlyText}
          </S.NoFieldText>
        </S.SelectWrapper>
      ) : (
        <S.DiskWrapper>{diskComponent}</S.DiskWrapper>
      )}
    </S.ResourceRow>
  );
};

export default ResourceRow;
